import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { useSelectedStore } from '@web/app/shared';
import { ZipParseResult, formatBytes, parseZipFile, readFileFromZip } from '@web/app/shared/utils/zipParser';
import { FileArchive, Info, Upload, X } from 'lucide-react';

import { extractPackageJson, extractVersion, fileToBase64, uploadAIStudioCode } from '@codes/ai-studio';
import { useCustomMutation } from '@codes/shared';
import {
    Button,
    Input,
    Label,
    Sheet,
    SheetClose,
    SheetContent,
    SheetFooter,
    SheetHeader,
    SheetTitle,
    cn,
} from '@codes/ui-kit';
import { isProdEnvironment } from '@codes/web-core';

import { FileTreeView } from './FileTreeView';
import { FileViewerDialog } from './FileViewerDialog';
import { CopyButton, GeminiKeyInfoContent } from './settings';

interface AIStudioDialogProps {
    open: boolean;
    onOpenChange: (val: boolean) => void;
}

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

export const AIStudioDialog = ({ open, onOpenChange }: AIStudioDialogProps) => {
    const { t } = useTranslation();
    const { projectId } = useParams<{ workspaceId: string; projectId: string }>();
    const { workspace } = useSelectedStore();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [appName, setAppName] = useState('');
    const [description, setDescription] = useState('');
    const [geminiApiKey, setGeminiApiKey] = useState(workspace?.geminiApiKey || '');
    const [mocks, setMocks] = useState(true);
    const [zipContents, setZipContents] = useState<ZipParseResult | null>(null);
    const [isParsing, setIsParsing] = useState(false);
    const [isFileViewerOpen, setIsFileViewerOpen] = useState(false);
    const [selectedFilePath, setSelectedFilePath] = useState<string>('');
    const [fileContent, setFileContent] = useState<string | null>(null);
    const [isLoadingFile, setIsLoadingFile] = useState(false);
    const [fileError, setFileError] = useState<string>('');
    const [isGeminiKeyInfoOpen, setIsGeminiKeyInfoOpen] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const isProd = isProdEnvironment();

    const resetForm = (): void => {
        setSelectedFile(null);
        setAppName('');
        setDescription('');
        setGeminiApiKey('');
        setMocks(true);
        setZipContents(null);
        setIsParsing(false);
    };

    const uploadMutation = useCustomMutation(uploadAIStudioCode, {
        onSuccess: data => {
            toast({
                title: t('aiStudio.success.uploaded'),
                description: t('aiStudio.success.details', { title: data.title, version: data.version }),
            });
            resetForm();
            onOpenChange(false);
        },
        onError: error => {
            toast({
                title: t('aiStudio.error.uploadFailed'),
                description: error?.toString(),
                variant: 'destructive',
            });
        },
    });

    const handleFileSelect = (file: File): void => {
        if (file.type !== 'application/zip' && file.type !== 'application/x-zip-compressed') {
            toast({
                title: t('aiStudio.error.invalidFileType'),
                description: t('aiStudio.error.mustBeZip'),
                variant: 'destructive',
            });
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
            toast({
                title: t('aiStudio.error.fileTooLarge'),
                description: t('aiStudio.error.maxSize', { size: '100MB' }),
                variant: 'destructive',
            });
            return;
        }

        setSelectedFile(file);

        if (!appName) {
            const nameWithoutExt = file.name.replace('.zip', '');
            setAppName(nameWithoutExt);
        }
    };

    useEffect(() => {
        if (open && workspace?.geminiApiKey) {
            setGeminiApiKey(workspace.geminiApiKey);
        }
    }, [open, workspace?.geminiApiKey]);

    useEffect(() => {
        if (!selectedFile) {
            setZipContents(null);
            return;
        }

        let cancelled = false;
        setIsParsing(true);

        const parseFile = async () => {
            try {
                const result = await parseZipFile(selectedFile);

                if (cancelled) {
                    return;
                }

                if (result.error) {
                    toast({
                        title: t('aiStudio.error.parseError'),
                        description: result.error,
                        variant: 'destructive',
                    });
                }
                setZipContents(result);
            } catch (error) {
                if (cancelled) {
                    return;
                }

                toast({
                    title: t('aiStudio.error.parseError'),
                    description: error instanceof Error ? error.message : t('common.error'),
                    variant: 'destructive',
                });
            } finally {
                if (!cancelled) {
                    setIsParsing(false);
                }
            }
        };

        parseFile();

        return () => {
            cancelled = true;
        };
    }, [selectedFile, t]);

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const file = e.target?.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
        // Reset input to allow re-selecting the same file
        e.target.value = '';
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        setIsDragging(false);

        const file = e.dataTransfer.files[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleCreate = async (): Promise<void> => {
        if (!projectId) {
            toast({
                title: t('aiStudio.error.required'),
                description: 'Project ID is required',
                variant: 'destructive',
            });
            return;
        }

        if (!selectedFile) {
            toast({
                title: t('aiStudio.error.noFileSelected'),
                description: t('aiStudio.error.appNameRequiredDesc'),
                variant: 'destructive',
            });
            return;
        }

        try {
            const base64Data = await fileToBase64(selectedFile);
            const packageJson = zipContents?.zipInstance ? await extractPackageJson(zipContents.zipInstance) : null;

            const title = packageJson?.name || appName.trim();
            const version = extractVersion(packageJson);

            await uploadMutation.mutateAsync({
                data: base64Data,
                title,
                version,
                projectId,
                mocks,
                geminiApiKey: geminiApiKey.trim(),
                ...(description.trim() && { description: description.trim() }),
            });
        } catch (error) {
            // Error already handled by mutation's onError callback
            console.error('Upload failed:', error);
        }
    };

    const handleClose = (val: boolean): void => {
        if (!val && !uploadMutation.isPending) {
            resetForm();
        }
        onOpenChange(val);
    };

    const handleFileClick = async (filePath: string): Promise<void> => {
        if (!zipContents?.zipInstance) {
            return;
        }

        setSelectedFilePath(filePath);
        setIsFileViewerOpen(true);
        setIsLoadingFile(true);
        setFileError('');
        setFileContent(null);

        try {
            const content = await readFileFromZip(zipContents.zipInstance, filePath);
            setFileContent(content);
        } catch (error) {
            setFileError(error instanceof Error ? error.message : t('common.error'));
        } finally {
            setIsLoadingFile(false);
        }
    };

    return (
        <>
            <Sheet open={open} onOpenChange={handleClose}>
                <SheetContent className="overflow-y-scroll">
                    <SheetHeader className="justify-between w-full flex items-center" aria-describedby={undefined}>
                        <div className="flex items-center gap-3">
                            <SheetTitle>{t('aiStudio.title')}</SheetTitle>
                            {!isProd && (
                                <div className="flex items-center gap-2 px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-md">
                                    <input
                                        id="mocks-header"
                                        type="checkbox"
                                        checked={mocks}
                                        onChange={e => setMocks(e.target.checked)}
                                        disabled={uploadMutation.isPending}
                                        className="h-3 w-3"
                                    />
                                    <Label htmlFor="mocks-header" className="text-xs cursor-pointer m-0">
                                        Mock
                                    </Label>
                                </div>
                            )}
                        </div>
                        <SheetClose>
                            <X className="w-5 h-5" />
                        </SheetClose>
                    </SheetHeader>

                    <div className="flex flex-col gap-y-4 py-6">
                        <div className="text-sm text-gray-600">
                            {t('aiStudio.description')}{' '}
                            <a
                                href="https://aistudio.google.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 underline"
                            >
                                {t('aiStudio.descriptionLink')}
                            </a>{' '}
                            {t('aiStudio.descriptionAfter')}
                        </div>

                        {/* Gemini API Key Input */}
                        <div className="flex flex-col gap-y-1">
                            <Label variant="form" className="flex items-center gap-1">
                                {t('aiStudio.geminiApiKey.label')}
                                <span className="text-error">*</span>
                                <button onClick={() => setIsGeminiKeyInfoOpen(true)}>
                                    <Info className="w-[14px] h-[14px] text-placeholder" />
                                </button>
                            </Label>
                            <div className="relative">
                                <Input
                                    type="password"
                                    placeholder={t('aiStudio.geminiApiKey.placeholder')}
                                    value={geminiApiKey}
                                    onChange={e => setGeminiApiKey(e.target.value)}
                                    disabled={uploadMutation.isPending}
                                    className={cn(geminiApiKey && !uploadMutation.isPending ? 'pr-12' : '')}
                                />
                                {!uploadMutation.isPending && geminiApiKey.trim() && (
                                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                        <CopyButton value={geminiApiKey} />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Description Input */}
                        <div className="flex flex-col gap-y-1">
                            <Label variant="form">{t('aiStudio.appDescription.label')}</Label>
                            <Input
                                placeholder={t('aiStudio.appDescription.placeholder')}
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                disabled={uploadMutation.isPending}
                            />
                        </div>

                        {/* File Upload Area */}
                        <div className="flex flex-col gap-y-1">
                            <div className="flex items-center justify-between gap-2">
                                <Label variant="form" className="flex items-center gap-1">
                                    {t('aiStudio.zipFile.label')}
                                    <span className="text-error">*</span>
                                </Label>
                                {appName && (
                                    <div className="text-sm text-gray-600">
                                        <span className="font-medium">{appName}</span>
                                    </div>
                                )}
                            </div>

                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileInputChange}
                                accept=".zip,application/zip,application/x-zip-compressed"
                                className="hidden"
                                disabled={uploadMutation.isPending}
                            />

                            {!selectedFile ? (
                                <div
                                    onClick={() => !uploadMutation.isPending && fileInputRef.current?.click()}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    className={`
                                    min-h-64 flex flex-col items-center justify-center
                                    border-2 border-dashed rounded-lg cursor-pointer
                                    transition-colors
                                    ${
                                        isDragging
                                            ? 'border-point bg-point/5'
                                            : 'border-gray-300 hover:border-point hover:bg-gray-50'
                                    }
                                    ${uploadMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}
                                `}
                                >
                                    <Upload
                                        className={`w-12 h-12 mb-4 ${isDragging ? 'text-point' : 'text-gray-400'}`}
                                    />
                                    <div className="text-center">
                                        <div className="text-lg font-medium text-gray-700 mb-2">
                                            {isDragging
                                                ? t('aiStudio.zipFile.dragText')
                                                : t('aiStudio.zipFile.uploadText')}
                                        </div>
                                        <div className="text-sm text-gray-500">{t('aiStudio.zipFile.dragHint')}</div>
                                        <div className="text-xs text-gray-400 mt-2">
                                            {t('aiStudio.zipFile.fileTypeHint')}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="border-2 border-gray-200 rounded-lg p-4">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-point/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <FileArchive className="w-6 h-6 text-point" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-gray-900 truncate">
                                                {selectedFile.name}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                {formatBytes(selectedFile.size)}
                                            </div>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                                setSelectedFile(null);
                                                setZipContents(null);
                                            }}
                                            className="flex-shrink-0"
                                            disabled={uploadMutation.isPending}
                                        >
                                            <X className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {/* Zip Contents Display */}
                            {selectedFile && zipContents && !zipContents.error && (
                                <div className="mt-4">
                                    <Label variant="form" className="text-[16px] mb-2">
                                        {t('aiStudio.zipContents.label')}
                                    </Label>
                                    <FileTreeView
                                        entries={zipContents.entries}
                                        totalFiles={zipContents.totalFiles}
                                        totalSize={zipContents.totalSize}
                                        onFileClick={handleFileClick}
                                    />
                                </div>
                            )}

                            {/* Parsing indicator */}
                            {isParsing && (
                                <div className="mt-4 text-center text-sm text-gray-500">
                                    {t('aiStudio.zipContents.parsing')}
                                </div>
                            )}
                        </div>
                    </div>

                    <SheetFooter className="px-6">
                        <Button
                            size="lg"
                            className="w-full mb-6"
                            onClick={handleCreate}
                            disabled={
                                !selectedFile || !appName.trim() || !geminiApiKey.trim() || uploadMutation.isPending
                            }
                        >
                            {uploadMutation.isPending ? t('aiStudio.uploading') : t('aiStudio.uploadButton')}
                        </Button>
                    </SheetFooter>
                </SheetContent>
            </Sheet>

            <Sheet open={isGeminiKeyInfoOpen} onOpenChange={setIsGeminiKeyInfoOpen}>
                <SheetContent className="overflow-auto" aria-describedby={undefined}>
                    <SheetHeader className="justify-end">
                        <SheetClose>
                            <X className="w-5 h-5" />
                        </SheetClose>
                    </SheetHeader>
                    <GeminiKeyInfoContent />
                </SheetContent>
            </Sheet>

            <FileViewerDialog
                open={isFileViewerOpen}
                onOpenChange={setIsFileViewerOpen}
                fileName={selectedFilePath.split('/').pop() || selectedFilePath}
                fileContent={fileContent}
                isLoading={isLoadingFile}
                error={fileError}
            />
        </>
    );
};
